<template>
  <v-card
    class="project-card elevation-0 d-flex flex-column"
    height="100%"
  >
    <v-card-title class="subtitle-1 py-4 px-6">
      <v-row class="flex-nowrap card-header-row">
        <!-- Left column: client name, project name, project code -->
        <v-col class="text-truncate pa-0 pr-1">
          <!-- Client Name / Loading State -->
          <div class="project-client-row caption text-truncate">
            <template v-if="clientName">
              <router-link
                v-if="organizationHref"
                :to="organizationHref"
                class="text-decoration-none client-link"
              >
                <span class="font-weight-medium primary--text">{{ clientName }}</span>
              </router-link>
              <span
                v-else
                class="font-weight-medium midgray--text client-link"
              >{{ clientName }}</span>
            </template>
            <v-skeleton-loader
              v-else-if="loadingClient"
              type="text"
              width="120"
            />
            <span
              v-else
              class="midgray--text"
            >
              <em>No client assigned</em>
              <v-btn
                icon
                x-small
                class="ml-1"
                @click="$emit('assign-client', project)"
              >
                <font-awesome-icon
                  :icon="['fas', 'plus']"
                  size="sm"
                />
              </v-btn>
            </span>
          </div>

          <!-- Project Name -->
          <div
            class="text-truncate clickable project-name-row"
            :title="project.projectName"
            @click="goToProject(project)"
          >
            <span class="darkblue--text project-name-text">{{ project.projectName }}</span>
            <!-- Child count badge (parent engagement) -->
            <v-chip
              v-if="childCount > 0"
              x-small
              color="indigo"
              text-color="white"
              class="ml-2"
              :title="`${childCount} child initiative${childCount === 1 ? '' : 's'}`"
            >
              {{ childCount }} {{ childCount === 1 ? 'child' : 'children' }}
            </v-chip>
            <!-- New/Updated Badges -->
            <v-chip
              v-if="isBadgeNew"
              x-small
              color="primary"
              text-color="white"
              class="ml-2"
            >
              New
            </v-chip>
            <v-chip
              v-else-if="isBadgeUpdated"
              x-small
              color="success"
              text-color="white"
              class="ml-2"
            >
              Updated
            </v-chip>
          </div>

          <!-- Project Code -->
          <div
            v-if="showProjectCode"
            class="caption project-code-row text-truncate"
          >
            <span
              v-if="project.projectCode"
              :class="projectCodeTextClass"
              style="font-weight: 600 !important;"
            >{{ project.projectCode }}</span>
            <span
              v-else-if="project.tempCode"
              class="orange--text text--darken-1"
              style="font-weight: 600 !important;"
              :title="`Temp code: ${project.tempCode} — not yet promoted`"
            >{{ project.tempCode }}</span>
            <span
              v-else
              :class="projectCodeTextClass"
              style="font-weight: 600 !important;"
            >Code TBD</span>
          </div>

          <!-- Parent engagement label -->
          <div
            v-if="parentName"
            class="caption mt-1 text-truncate"
          >
            <font-awesome-icon
              :icon="['fas', 'sitemap']"
              size="xs"
              class="grey--text mr-1"
            />
            <span class="grey--text">{{ parentName }}</span>
          </div>
        </v-col>

        <!-- Right column: Actions menu -->
        <v-col class="col-auto pa-0 d-flex align-start flex-shrink-0">
          <!-- Actions Menu -->
          <v-menu
            offset-y
            bottom
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                v-on="on"
              >
                <font-awesome-icon
                  :icon="['fas', 'ellipsis-vertical']"
                />
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item
                v-for="action in actions"
                :key="typeof action.name === 'function' ? action.name(project) : action.name"
                link
                @click="action.event(project)"
              >
                <v-list-item-icon class="mr-3 align-center">
                  <font-awesome-icon
                    :icon="typeof action.icon === 'function' ? action.icon(project) : action.icon"
                    size="sm"
                  />
                </v-list-item-icon>
                <v-list-item-title>{{ typeof action.name === 'function' ? action.name(project) : action.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
    </v-card-title>

    <!-- Card Header Divider -->
    <v-divider />

    <v-card-text class="px-4">
      <v-col>
        <v-row v-if="showProjectDescription">
          <v-col>
            <v-row class="subtitle-2">
              <v-col class="midgray--text">
                Description
              </v-col>
            </v-row>

            <v-row class="body-2 mt-0">
              <v-col :class="`pt-0 font-weight-medium ${textColor}--text`">
                {{ project.projectDescription }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-if="showProjectDocs && projectDocs.length">
          <v-col>
            <v-row class="subtitle-2">
              <v-col class="midgray--text">
                Docs
              </v-col>
            </v-row>

            <v-row class="body-2 mt-0">
              <v-col class="pt-0">
                <a
                  v-for="(doc, index) in projectDocs.slice(0, 3)"
                  :key="`${project.projectId || project.projectCode}-card-doc-${index}`"
                  :href="doc.url"
                  class="d-block primary--text text-decoration-none mb-1"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >
                  {{ doc.title || doc.url }}
                </a>

                <span
                  v-if="projectDocs.length > 3"
                  class="caption midgray--text"
                >
                  +{{ projectDocs.length - 3 }} more link{{ projectDocs.length - 3 === 1 ? '' : 's' }}
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Progress Bar -->
        <v-row v-if="showProgressSection && project.isLoadingTimeEntries">
          <v-col>
            <v-row>
              <v-col class="pb-0 midgray--text">
                Loading time entries...
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-skeleton-loader
                  type="heading"
                  :loading="project.isLoadingTimeEntries"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-else-if="showProgressSection || showDatesRow">
          <v-col>
            <v-row
              v-if="showTimeConsumed"
              class="subtitle-2"
            >
              <v-col class="pb-0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.totalBillableTimeFormatted }}
                    </span>
                  </template>
                  <span>
                    Total billable hours logged for this project
                  </span>
                </v-tooltip>
                <span class="px-1 midgray--text">/</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.projectHoursFormatted }}
                    </span>
                  </template>
                  <span>
                    Total allocated hours for this project
                  </span>
                </v-tooltip>
                <span class="pl-1 midgray--text">Hrs</span>
              </v-col>

              <v-col class="pb-0 midgray--text text-right">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`pr-1 ${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.projectMinutesConsumedPercentFormatted }}%
                    </span>
                  </template>
                  <span>
                    Percentage of project hours consumed
                  </span>
                </v-tooltip>
                <span class="midgray--text">Hrs</span>
              </v-col>
            </v-row>

            <v-row
              v-if="showProgress"
              class="body-2 mt-2 cursor-pointer"
              @click="showTimeEntries(project)"
            >
              <v-col :class="`font-weight-medium ${textColor}--text`">
                <div
                  class="progress-track"
                  style="width: 100%;"
                >
                  <div
                    :class="[
                      'progress-fill',
                      project.projectStatusColorBackground,
                      { 'progress-empty': project.projectMinutesConsumedPercentage === 0 }
                    ]"
                    :style="`width: ${ Math.min(project.projectMinutesConsumedPercentage, 100) }%;`"
                  />
                </div>
              </v-col>
            </v-row>

            <v-row
              v-if="showDatesRow"
              class="mt-0 subtitle-2"
            >
              <v-col
                v-if="showStartDate || showEndDate"
                class="col-auto pt-2"
              >
                <v-tooltip
                  v-if="showStartDate"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ displayStartDate }}
                    </span>
                  </template>
                  <span>
                    Project start date
                  </span>
                </v-tooltip>

                <span
                  v-if="showStartDate && showEndDate"
                  class="px-1 midgray--text"
                >to</span>

                <v-tooltip
                  v-if="showEndDate"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ displayEndDate }}
                    </span>
                  </template>
                  <span>
                    Project end date
                  </span>
                </v-tooltip>
              </v-col>

              <v-col
                v-if="showDaysRemaining"
                class="text-right pt-2"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="[
                        'days-left-value',
                        isProjectOverdue ? 'error--text font-weight-bold' : `${textColor}--text`
                      ]"
                      v-on="on"
                    >
                      <font-awesome-icon
                        v-if="isProjectOverdue"
                        :icon="['fas', 'triangle-exclamation']"
                        size="xs"
                        class="mr-1"
                      />
                      {{ Math.abs(project.projectDaysRemaining) }}
                    </span>
                  </template>
                  <span>
                    {{ isProjectOverdue ? `Overdue by ${Math.abs(project.projectDaysRemaining)} days` : 'Days remaining until project end date' }}
                  </span>
                </v-tooltip>
                <span
                  class="days-left-label"
                  :class="isProjectOverdue ? 'error--text' : 'midgray--text'"
                >
                  {{ isProjectOverdue ? 'Overdue' : (project.projectDaysRemaining === 1 ? 'Day Left' : 'Days Left') }}
                </span>
              </v-col>
            </v-row>

            <!-- Last Updated Row -->
            <v-row
              v-if="displayLastUpdated"
              class="mt-2 subtitle-2"
            >
              <v-col class="col-auto pt-2">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      class="caption midgray--text"
                      v-on="on"
                    >
                      {{ displayLastUpdated }}
                    </span>
                  </template>
                  <span>
                    Last updated {{ project.updatedAt ? new Date(project.updatedAt).toLocaleString() : 'Unknown' }}
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>

    <v-divider />

    <!-- Footer: Type & Status -->
    <v-card-actions
      v-if="showProjectStatus || showProjectType"
      class="px-6 justify-space-between"
    >
      <v-col
        v-if="showProjectType"
        class="pa-0 pr-2 text-truncate"
      >
        <span class="caption midgray--text text-truncate d-block">
          {{ projectType || 'None' }}
        </span>
      </v-col>

      <v-chip
        v-if="showProjectStatus"
        :class="`${project.projectStatusColorText}--text`"
        :color="project.projectStatusColorBackground"
        small
      >
        {{ projectStatus }}
      </v-chip>
    </v-card-actions>
  </v-card>
</template>

<script>
import themeClasses from '@/mixins/themeClasses'

export default {
  name: 'ProjectCard',
  mixins: [themeClasses],
  props: {
    actions: {
      type: Array,
      required: true,
    },
    childCount: {
      type: Number,
      default: 0,
    },
    clientName: {
      type: String,
      default: null,
    },
    loadingClient: {
      type: Boolean,
      default: false,
    },
    organizationHref: {
      type: String,
      default: null,
    },
    parentName: {
      type: String,
      default: null,
    },
    project: {
      type: Object,
      required: true,
    },
    projectStatus: {
      type: String,
      required: true,
    },
    projectType: {
      type: String,
      required: false,
      default: 'None',
    },
    /**
     * Array of visible column values from ToolsDrawer
     */
    visibleColumns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // No data properties for column visibility - all are computed from visibleColumns prop
    }
  },
  computed: {
    projectCodeTextClass() {
      return this.themeTextClass('darkestblue--text', 'midgray--text')
    },
    textColor() {
      return this.themeSurfaceColor('nearblack', 'gray')
    },
    isProjectOverdue() {
      // Exclude projects with "complete" or "lost" status from overdue calculation
      const normalizedStatus = (this.projectStatus || '').toLowerCase().trim()
      if (normalizedStatus === 'complete' || normalizedStatus === 'lost') {
        return false
      }
      return this.project.projectDaysRemaining < 0
    },
    displayStartDate() {
      const value = this.project?.startDateFormatted
      if (!value || value === 'Invalid Date') {
        return '--'
      }
      return value
    },
    displayEndDate() {
      const value = this.project?.endDateFormatted
      if (!value || value === 'Invalid Date') {
        return '--'
      }
      return value
    },
    // Computed property to ensure reactivity when project updates
    reactiveProject() {
      return { ...this.project }
    },
    /**
     * Check if a column is visible
     * If visibleColumns is empty, all columns are shown (default behavior)
     */
    hasVisibleColumns() {
      return this.visibleColumns && this.visibleColumns.length > 0
    },
    showProjectCode() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('projectCode')
    },
    showProjectDescription() {
      return this.hasVisibleColumns && this.visibleColumns.includes('projectDescription')
    },
    showProjectType() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('projectType')
    },
    showProjectDocs() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('docs')
    },
    showProjectStatus() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('projectStatus')
    },
    showProgress() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('progress')
    },
    showTimeConsumed() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('timeConsumed')
    },
    showStartDate() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('startDateFormatted')
    },
    showEndDate() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('endDateFormatted')
    },
    showDaysRemaining() {
      return !this.hasVisibleColumns || this.visibleColumns.includes('daysRemaining')
    },
    // Show dates row if any date-related field is visible
    showDatesRow() {
      return this.showStartDate || this.showEndDate || this.showDaysRemaining
    },
    // Show progress section if progress or time consumed is visible
    showProgressSection() {
      return this.showProgress || this.showTimeConsumed
    },
    projectDocs() {
      const docs = Array.isArray(this.project?.docs) ? this.project.docs : []
      return docs
        .map((doc) => ({
          title: doc?.title || doc?.name || '',
          url: doc?.url || doc?.href || '',
        }))
        .filter((doc) => doc.title || doc.url)
    },
    isBadgeNew() {
      if (!this.project?.createdAt) return false
      const createdTime = new Date(this.project.createdAt).getTime()
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000)
      return createdTime > sevenDaysAgo
    },
    isBadgeUpdated() {
      if (this.isBadgeNew) return false // Don't show updated if already new
      if (!this.project?.updatedAt) return false
      const updatedTime = new Date(this.project.updatedAt).getTime()
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000)
      return updatedTime > oneDayAgo
    },
    displayLastUpdated() {
      if (!this.project?.updatedAt) return null
      const updated = new Date(this.project.updatedAt)
      const now = Date.now()
      const diffMs = now - updated.getTime()
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 1) return 'Updated just now'
      if (diffMins < 60) return `Updated ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
      if (diffHours < 24) return `Updated ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffDays < 7) return `Updated ${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      return `Updated on ${updated.toLocaleDateString()}`
    },
  },
  watch: {
    project: {
      deep: true,
      immediate: true,
      handler(newProject, oldProject) {
        // Force re-render when project data changes (especially loading states)
        if (newProject && oldProject) {
          const loadingChanged = newProject.isLoadingTimeEntries !== oldProject.isLoadingTimeEntries
          const timeDataChanged = newProject.totalBillableTimeFormatted !== oldProject.totalBillableTimeFormatted

          if (loadingChanged || timeDataChanged) {
            this.$forceUpdate()
          }
        }
      },
    },
  },
  methods: {
    goToProject(project) {
      this.$emit('go-to-project', project)
    },
    showTimeEntries(project) {
      this.$emit('show-time-entries', project)
    },
  },
}
</script>

<style lang="scss" scoped>
.project-card {
  transition: box-shadow 0.2s ease, background-color 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background-color: rgba(255, 255, 255, 0.98);

  .card-header-row {
    width: 100%;
    align-items: flex-start;
    margin: 0;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  }

  .clickable {
    cursor: pointer;

    &:hover {
      color: #1976d2;
    }
  }

  .project-client-row {
    margin-top: 2px;
    margin-bottom: 2px;
    line-height: 1.2;
  }

  .client-link {
    display: inline-block;
    line-height: 1.2;
  }

  .project-name-row {
    margin-top: 2px;
    margin-bottom: 2px;
    line-height: 1.1;
  }

  .project-name-text {
    font-weight: 700;
    letter-spacing: 0.005em;
  }

  .project-code-row {
    margin-top: 4px;
    line-height: 1.15;
    letter-spacing: 0.01em;
  }

  .days-left-value {
    margin-right: 1px;
  }

  .days-left-label {
    line-height: 1;
  }

  .col-grow {
    flex-grow: 1;
    min-width: 0;
  }

  .v-card__text {
    flex-grow: 1;
  }

  // Progress bar styling
  .progress-track {
    height: 6px;
    background-color: rgba(0, 0, 0, 0.08);
    border-radius: 3px;
    overflow: hidden;
    position: relative;
  }

  .progress-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
    min-width: 0;

    // When progress is 0%, show a subtle indicator
    &.progress-empty {
      width: 2px !important;
      background-color: rgba(0, 0, 0, 0.12) !important;
    }
  }
}

// Dark mode support
.theme--dark {
  .project-card {
    border-color: rgba(255, 255, 255, 0.08);
    background-color: rgba(30, 30, 30, 0.98);

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }

    .progress-track {
      background-color: rgba(255, 255, 255, 0.12);
    }

    .progress-fill {
      &.progress-empty {
        background-color: rgba(255, 255, 255, 0.18) !important;
      }
    }
  }
}
</style>
